import { useState } from 'react'
import { useAdminAccessibilityCoverage, useAdminAccessibilityTrends } from '@/hooks/useAdminAccessibility'
import { format } from 'date-fns'
import type { AccessibilityCoverageRow } from '@/hooks/useAdminAccessibility'

const TREND_DAYS = [30, 90] as const

function toISODate(d: Date): string {
  return d.toISOString().slice(0, 10)
}

function getDateRange(days: number): { from: Date; to: Date } {
  const to = new Date()
  const from = new Date(to)
  from.setDate(from.getDate() - days)
  return { from, to }
}

type ChannelSortKey = 'channel_name' | 'ad_count' | 'subtitles_count' | 'bsl_count'

function sortCoverageRows(
  rows: AccessibilityCoverageRow[],
  key: ChannelSortKey,
  dir: 'asc' | 'desc'
): AccessibilityCoverageRow[] {
  const totals = rows.find((r) => r.channel_id === '')
  const rest = rows.filter((r) => r.channel_id !== '')
  const mult = dir === 'asc' ? 1 : -1
  rest.sort((a, b) => {
    const aVal = a[key]
    const bVal = b[key]
    if (typeof aVal === 'string' && typeof bVal === 'string') return mult * aVal.localeCompare(bVal)
    return mult * (Number(aVal) - Number(bVal))
  })
  return totals ? [totals, ...rest] : rest
}

export default function AdminAccessibilityPage() {
  const [trendDays, setTrendDays] = useState<number>(30)
  const [channelSort, setChannelSort] = useState<{ key: ChannelSortKey; dir: 'asc' | 'desc' }>({
    key: 'channel_name',
    dir: 'asc',
  })

  const { data: coverage, isLoading: coverageLoading, error: coverageError } = useAdminAccessibilityCoverage()
  const { from, to } = getDateRange(trendDays)
  const { data: trends, isLoading: trendsLoading, error: trendsError } = useAdminAccessibilityTrends(from, to)

  const isLoading = coverageLoading || trendsLoading
  const error = coverageError || trendsError

  if (isLoading) {
    return (
      <div role="status" aria-live="polite" className="p-4">
        <p style={{ color: 'var(--text-secondary)' }}>Loading accessibility metrics…</p>
      </div>
    )
  }

  if (error) {
    return (
      <section aria-labelledby="a11y-error-heading">
        <h1 id="a11y-error-heading" className="text-2xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
          Accessibility impact
        </h1>
        <p role="alert" style={{ color: 'var(--color-error)' }}>
          Failed to load: {error instanceof Error ? error.message : String(error)}.
        </p>
      </section>
    )
  }

  const totalsRow = coverage?.find((r) => r.channel_id === '')
  const channelRows = coverage?.filter((r) => r.channel_id !== '') ?? []
  const sortedChannels = sortCoverageRows(channelRows, channelSort.key, channelSort.dir)

  const handleSort = (key: ChannelSortKey) => {
    setChannelSort((prev) => ({
      key,
      dir: prev.key === key && prev.dir === 'asc' ? 'desc' : 'asc',
    }))
  }

  return (
    <section aria-labelledby="a11y-heading">
      <h1 id="a11y-heading" className="text-2xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
        Accessibility impact
      </h1>

      <p className="mb-6 text-sm" style={{ color: 'var(--text-secondary)' }} id="a11y-disclaimer">
        All data on this page is anonymised and aggregated. No individual user or personally identifiable information is shown.
        Coverage totals and by-channel use the same time window and displayed channels as the TV Listings screen (today through end of listing days).
      </p>

      {/* Coverage totals */}
      <section aria-labelledby="coverage-heading" className="mb-8">
        <h2 id="coverage-heading" className="text-xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
          Coverage totals
        </h2>
        <p className="mb-2" style={{ color: 'var(--text-secondary)' }}>
          Total programme slots with at least one accessibility type (Audio Description, Subtitles, or BSL).
        </p>
        {totalsRow ? (
          <table
            className="w-full max-w-md border-collapse"
            style={{ borderColor: 'var(--bg-tertiary)' }}
            summary="Total counts of listings with AD, Subtitles, and BSL."
          >
            <thead>
              <tr style={{ borderBottom: '2px solid var(--bg-tertiary)' }}>
                <th scope="col" className="text-left p-2" style={{ color: 'var(--text-primary)' }}>Type</th>
                <th scope="col" className="text-right p-2" style={{ color: 'var(--text-primary)' }}>Count</th>
              </tr>
            </thead>
            <tbody>
              <tr style={{ borderBottom: '1px solid var(--bg-tertiary)' }}>
                <td className="p-2" style={{ color: 'var(--text-primary)' }}>Audio Description (AD)</td>
                <td className="p-2 text-right" style={{ color: 'var(--text-primary)' }}>{totalsRow.ad_count.toLocaleString()}</td>
              </tr>
              <tr style={{ borderBottom: '1px solid var(--bg-tertiary)' }}>
                <td className="p-2" style={{ color: 'var(--text-primary)' }}>Subtitles</td>
                <td className="p-2 text-right" style={{ color: 'var(--text-primary)' }}>{totalsRow.subtitles_count.toLocaleString()}</td>
              </tr>
              <tr style={{ borderBottom: '1px solid var(--bg-tertiary)' }}>
                <td className="p-2" style={{ color: 'var(--text-primary)' }}>BSL</td>
                <td className="p-2 text-right" style={{ color: 'var(--text-primary)' }}>{totalsRow.bsl_count.toLocaleString()}</td>
              </tr>
            </tbody>
          </table>
        ) : (
          <p style={{ color: 'var(--text-secondary)' }}>No coverage data available.</p>
        )}
      </section>

      {/* By channel */}
      <section aria-labelledby="by-channel-heading" className="mb-8">
        <h2 id="by-channel-heading" className="text-xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
          By channel
        </h2>
        <p className="mb-2" style={{ color: 'var(--text-secondary)' }}>
          Counts per channel. Sort by column (activate column header).
        </p>
        <table
          className="w-full border-collapse"
          style={{ borderColor: 'var(--bg-tertiary)' }}
          summary="Accessibility coverage counts per channel: AD, Subtitles, BSL."
        >
          <thead>
            <tr style={{ borderBottom: '2px solid var(--bg-tertiary)' }}>
              <th scope="col" className="text-left p-2" style={{ color: 'var(--text-primary)' }}>
                <button
                  type="button"
                  onClick={() => handleSort('channel_name')}
                  className="font-bold text-left min-h-[44px] px-2 -mx-2 rounded"
                  style={{ color: 'var(--text-primary)' }}
                  aria-sort={channelSort.key === 'channel_name' ? (channelSort.dir === 'asc' ? 'ascending' : 'descending') : undefined}
                >
                  Channel {channelSort.key === 'channel_name' && (channelSort.dir === 'asc' ? '↑' : '↓')}
                </button>
              </th>
              <th scope="col" className="text-right p-2" style={{ color: 'var(--text-primary)' }}>
                <button
                  type="button"
                  onClick={() => handleSort('ad_count')}
                  className="font-bold text-right min-h-[44px] px-2 -mx-2 rounded"
                  style={{ color: 'var(--text-primary)' }}
                  aria-sort={channelSort.key === 'ad_count' ? (channelSort.dir === 'asc' ? 'ascending' : 'descending') : undefined}
                >
                  AD {channelSort.key === 'ad_count' && (channelSort.dir === 'asc' ? '↑' : '↓')}
                </button>
              </th>
              <th scope="col" className="text-right p-2" style={{ color: 'var(--text-primary)' }}>
                <button
                  type="button"
                  onClick={() => handleSort('subtitles_count')}
                  className="font-bold text-right min-h-[44px] px-2 -mx-2 rounded"
                  style={{ color: 'var(--text-primary)' }}
                  aria-sort={channelSort.key === 'subtitles_count' ? (channelSort.dir === 'asc' ? 'ascending' : 'descending') : undefined}
                >
                  Subtitles {channelSort.key === 'subtitles_count' && (channelSort.dir === 'asc' ? '↑' : '↓')}
                </button>
              </th>
              <th scope="col" className="text-right p-2" style={{ color: 'var(--text-primary)' }}>
                <button
                  type="button"
                  onClick={() => handleSort('bsl_count')}
                  className="font-bold text-right min-h-[44px] px-2 -mx-2 rounded"
                  style={{ color: 'var(--text-primary)' }}
                  aria-sort={channelSort.key === 'bsl_count' ? (channelSort.dir === 'asc' ? 'ascending' : 'descending') : undefined}
                >
                  BSL {channelSort.key === 'bsl_count' && (channelSort.dir === 'asc' ? '↑' : '↓')}
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedChannels.length === 0 ? (
              <tr>
                <td colSpan={4} className="p-4 text-center" style={{ color: 'var(--text-secondary)' }}>
                  No channel data.
                </td>
              </tr>
            ) : (
              sortedChannels.map((row) => (
                <tr key={row.channel_id || 'totals'} style={{ borderBottom: '1px solid var(--bg-tertiary)' }}>
                  <td className="p-2" style={{ color: 'var(--text-primary)' }}>{row.channel_name}</td>
                  <td className="p-2 text-right" style={{ color: 'var(--text-primary)' }}>{row.ad_count.toLocaleString()}</td>
                  <td className="p-2 text-right" style={{ color: 'var(--text-primary)' }}>{row.subtitles_count.toLocaleString()}</td>
                  <td className="p-2 text-right" style={{ color: 'var(--text-primary)' }}>{row.bsl_count.toLocaleString()}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </section>

      {/* Trends */}
      <section aria-labelledby="trends-heading" className="mb-8">
        <h2 id="trends-heading" className="text-xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
          Coverage over time
        </h2>
        <div className="mb-4">
          <label htmlFor="a11y-trend-days" className="block text-sm font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>
            Period
          </label>
          <select
            id="a11y-trend-days"
            value={trendDays}
            onChange={(e) => setTrendDays(Number(e.target.value))}
            className="min-h-[44px] px-3 rounded border-2"
            style={{
              borderColor: 'var(--bg-tertiary)',
              color: 'var(--text-primary)',
              backgroundColor: 'var(--bg-primary)',
            }}
          >
            {TREND_DAYS.map((d) => (
              <option key={d} value={d}>Last {d} days</option>
            ))}
          </select>
        </div>
        {trends && trends.length > 0 ? (
          <table
            className="w-full border-collapse"
            style={{ borderColor: 'var(--bg-tertiary)' }}
            summary={`Daily accessibility-tagged slot counts from ${toISODate(from)} to ${toISODate(to)}.`}
          >
            <thead>
              <tr style={{ borderBottom: '2px solid var(--bg-tertiary)' }}>
                <th scope="col" className="text-left p-2" style={{ color: 'var(--text-primary)' }}>Date</th>
                <th scope="col" className="text-right p-2" style={{ color: 'var(--text-primary)' }}>AD</th>
                <th scope="col" className="text-right p-2" style={{ color: 'var(--text-primary)' }}>Subtitles</th>
                <th scope="col" className="text-right p-2" style={{ color: 'var(--text-primary)' }}>BSL</th>
              </tr>
            </thead>
            <tbody>
              {trends.map((row) => (
                <tr key={row.stat_date} style={{ borderBottom: '1px solid var(--bg-tertiary)' }}>
                  <td className="p-2" style={{ color: 'var(--text-primary)' }}>{format(new Date(row.stat_date), 'd MMM yyyy')}</td>
                  <td className="p-2 text-right" style={{ color: 'var(--text-primary)' }}>{row.ad_count.toLocaleString()}</td>
                  <td className="p-2 text-right" style={{ color: 'var(--text-primary)' }}>{row.subtitles_count.toLocaleString()}</td>
                  <td className="p-2 text-right" style={{ color: 'var(--text-primary)' }}>{row.bsl_count.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p style={{ color: 'var(--text-secondary)' }}>No trend data for this period.</p>
        )}
      </section>
    </section>
  )
}
